// Clone + geometry only. Re-flow x from 0; do not trust segment anchors.
import { GeneratedLayout, GeneratedSegment, StudioItem, StudioRunGeometry } from "../studioTypes";
import { rowCapacityOf, rowOpeningOf } from "@CommonUtils/cdtStudio/domain/wallRows";
import { runBayWidthOf, slacksOf } from "../bayShare";

export const cloneLayout = (layout: GeneratedLayout): GeneratedLayout => ({
  placements: Object.fromEntries(
    Object.entries(layout.placements).map(([skuId, placement]) => [
      skuId,
      { skuId, segments: placement.segments.map((segment) => ({ ...segment })) },
    ]),
  ),
  walkOrder: [...layout.walkOrder],
  ceilingViolations: layout.ceilingViolations?.map((violation) => ({ ...violation })),
  shedFacings: layout.shedFacings?.map((shed) => ({ ...shed })),
});

/** Shelf opening per rank for one run — same rule as the generator's pack. */
export const openingsOf = (run: StudioRunGeometry): Map<number, number> =>
  new Map(run.shelves.map((shelf) => [shelf.rank, rowOpeningOf(run, shelf.rank) ?? Infinity]));

export interface LaneSegment {
  skuId: string;
  segment: GeneratedSegment;
  widthCm: number;
}

export const laneSegmentsOf = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
): Map<string, LaneSegment[]> => {
  const widthOf = new Map(items.map((item) => [item.skuId, item.facingWidthCm]));
  const lanes = new Map<string, LaneSegment[]>();
  for (const placement of Object.values(layout.placements)) {
    for (const segment of placement.segments) {
      const laneKey = `${segment.run}|${segment.shelf}`;
      const lane = lanes.get(laneKey) ?? [];
      lane.push({
        skuId: placement.skuId,
        segment,
        widthCm: segment.hFacings * (widthOf.get(placement.skuId) ?? 0),
      });
      lanes.set(laneKey, lane);
    }
  }
  return lanes;
};

/** Order every lane by `rank`, keeping each block's reading order, then re-flow x from 0. Mutates `layout`. */
export const applyBlockOrder = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  blockKeyOf: (skuId: string) => string,
  /** Rank per (run, block): a cross-run block holds a different position in each run's sequence. */
  rankOf: (run: number, blockKey: string) => number,
): void => {
  const lanes = laneSegmentsOf(layout, items);
  for (const lane of lanes.values()) {
    lane.sort(
      (a, b) =>
        rankOf(a.segment.run, blockKeyOf(a.skuId)) - rankOf(b.segment.run, blockKeyOf(b.skuId)) ||
        a.segment.xCm - b.segment.xCm,
    );
    let cursor = 0;
    for (const entry of lane) {
      entry.segment.xCm = +cursor.toFixed(1);
      cursor += entry.widthCm;
    }
  }
};

/**
 * Deal a block's segments across a shelf band, balanced by width, reading order
 * preserved. A pack too tall for its dealt shelf takes the nearest band shelf
 * that clears it, else keeps its own and the caller's audit reports the residue.
 */
export const dealAcrossBand = (
  segments: readonly LaneSegment[],
  band: readonly number[],
  run: StudioRunGeometry,
  heightOf: (skuId: string) => number,
): void => {
  if (band.length === 0 || segments.length === 0) return;
  const openings = openingsOf(run);
  const slack = slacksOf(runBayWidthOf(run));
  const ordered = [...segments].sort(
    (a, b) => a.segment.shelf - b.segment.shelf || a.segment.xCm - b.segment.xCm,
  );
  const total = ordered.reduce((sum, entry) => sum + entry.widthCm, 0);
  const share = total / band.length;
  let row = 0;
  let rowWidth = 0;
  for (const entry of ordered) {
    if (rowWidth + entry.widthCm / 2 > share && row < band.length - 1) {
      row += 1;
      rowWidth = 0;
    }
    const fits = (rank: number) =>
      (openings.get(rank) ?? Infinity) + slack.fill >= heightOf(entry.skuId);
    let landed = band[row];
    if (!fits(landed)) {
      const candidate = band
        .filter((rank) => fits(rank))
        .sort((a, b) => Math.abs(a - band[row]) - Math.abs(b - band[row]))[0];
      if (candidate !== undefined) landed = candidate;
      else {
        // Nothing in the band clears the pack: leave it where it stands.
        continue;
      }
    }
    if (landed === band[row]) rowWidth += entry.widthCm;
    entry.segment.shelf = landed;
  }
};

/** Lanes over-full in cm — what the renderer draws as pitch-compressed, OVERLAPPING packs. */
export const overflowCensusOf = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): { lanes: number; cm: number } => {
  const runById = new Map(runs.map((run) => [run.run, run]));
  let lanes = 0;
  let cm = 0;
  for (const [laneKey, lane] of laneSegmentsOf(layout, items)) {
    const [runId, rank] = laneKey.split("|").map(Number);
    const run = runById.get(runId);
    if (!run) continue;
    const cap = rowCapacityOf(run, rank);
    const fill = lane.reduce((sum, entry) => sum + entry.widthCm, 0);
    if (fill > cap + slacksOf(runBayWidthOf(run, runs)).fill) {
      lanes += 1;
      cm += fill - cap;
    }
  }
  return { lanes, cm };
};

/** Largest-remainder share of `n` shelves by weight, at least one each. */
export const shareShelves = (weights: readonly number[], n: number): number[] => {
  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  const raw = weights.map((weight) => (weight / total) * n);
  const share = raw.map((value) => Math.max(1, Math.floor(value)));
  let left = n - share.reduce((sum, value) => sum + value, 0);
  const remainders = raw
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction);
  let cursor = 0;
  while (left > 0 && remainders.length > 0) {
    share[remainders[cursor % remainders.length].index] += 1;
    left -= 1;
    cursor += 1;
  }
  while (left < 0) {
    const biggest = share.indexOf(Math.max(...share));
    if (share[biggest] <= 1) break;
    share[biggest] -= 1;
    left += 1;
  }
  return share;
};
